import { useCallback, useMemo, useState } from "react";
import { Redirect, RouteComponentProps, useHistory } from "react-router-dom";

import { Flow } from "../../../../../shared/components/Flow";
import { FlowStep } from "../../../../../shared/components/FlowSwitch/types";
import { ExtractStringRouteParams } from "../../../../../shared/types/router";
import { buildEngagementTypeDetailsURL } from "../../../../EngagementTypes/paths";
import { ENGAGEMENT_REQUIREMENTS_CREATE_BY_TEMPLATE_FLOW_PATH } from "../../../paths/constants";
import {
  buildRequirementDetailsPath,
  getCreateRequirementDefinitionFlowPathname,
  getCreateRequirementDefinitionPathname
} from "../../../paths/helpers";
import { RequirementDefinitionFormValues } from "../../../types";
import { getRequirementDefinitionTemplateByKey } from "../../../utils/getRequirementDefinitionTemplateByKey";

import { CreateRequirementDefinitionByTemplateForm } from "./CreateRequirementDefinitionByTemplateForm";
import { StepReviewAndCreate } from "./StepReviewAndCreate";

export const RouteCreateRequirementFlow: React.FC<
  RouteComponentProps<
    ExtractStringRouteParams<
      typeof ENGAGEMENT_REQUIREMENTS_CREATE_BY_TEMPLATE_FLOW_PATH,
      string
    >,
    {},
    { engagementId: string }
  >
> = props => {
  const { templateId } = props.match.params;
  const { state } = props.location;
  const engagementId = state?.engagementId;
  const history = useHistory();

  const [formState, setFormState] = useState<
    RequirementDefinitionFormValues | undefined
  >();

  const template = useMemo(() => {
    return getRequirementDefinitionTemplateByKey(templateId);
  }, [templateId]);

  const basePath = getCreateRequirementDefinitionFlowPathname(templateId);
  const backPath = getCreateRequirementDefinitionPathname("all");

  const onSuccess = useCallback(() => {}, []);

  const onBack = useCallback(() => {
    if (engagementId) {
      history.push(buildEngagementTypeDetailsURL(engagementId));
    } else {
      history.push(backPath);
    }
  }, []);

  const FirstStep: React.FC<FlowStep> = useMemo(
    () => props => (
      <CreateRequirementDefinitionByTemplateForm
        formData={formState}
        template={template!}
        onBack={props.onBack!}
        onSuccess={formValues => {
          setFormState(formValues);
          props.onNext!();
        }}
      />
    ),
    [template?.key, formState]
  );

  const SecondStep: React.FC<FlowStep> = useMemo(
    () => props => {
      if (template && formState) {
        return (
          <StepReviewAndCreate
            engagementId={engagementId}
            formData={formState}
            template={template}
            onBack={props.onBack!}
            onSuccess={requirementDefinition => {
              if (engagementId) {
                history.push(buildEngagementTypeDetailsURL(engagementId));
              } else {
                history.push(
                  buildRequirementDetailsPath(requirementDefinition.id!)
                );
              }
            }}
          />
        );
      } else {
        return <Redirect from="*" to={{ pathname: basePath, state }} />;
      }
    },
    [formState, template?.key, engagementId]
  );

  if (!template) {
    return <Redirect from="*" to={{ pathname: basePath, state }} />;
  }

  return (
    <Flow
      basePath={basePath}
      steps={[
        {
          slug: "flow",
          title: "Requirement settings",
          steps: [
            {
              slug: "info-and-settings",
              title: "Info & settings",
              component: FirstStep
            },
            {
              slug: "preview-and-create",
              title: "Preview & create",
              component: SecondStep
            }
          ]
        }
      ]}
      title={template!.name}
      onBack={onBack}
      onNext={onSuccess}
    />
  );
};
